/**
 * <hero-video> — plays the `hero` section's hand-built loop (snippets/hero-video.liquid) while it is near the
 * viewport. The markup ships a poster, `preload="none"`, no `autoplay` and its <source>s with `data-src`, so a
 * visitor who never scrolls to the video downloads the poster alone (PERF-02); scrolling it into view picks the
 * source for the current width and starts the muted loop, scrolling away pauses it.
 */
class HeroVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.video.muted = true; // muted playback needs no user gesture (autoplay policy)
    if (!('IntersectionObserver' in window)) {
      this.play();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.play();
          else this.video?.pause();
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.observer.observe(this.video);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = undefined;
  }

  play() {
    const { video } = this;
    if (!video) return;
    // First play: hand the sources their URLs and re-run source selection now, so each `media` is matched against
    // the laid-out viewport (see the snippet's doc).
    const pending = video.querySelectorAll('source[data-src]');
    if (pending.length) {
      for (const source of pending) {
        source.setAttribute('src', source.getAttribute('data-src') ?? '');
        source.removeAttribute('data-src');
      }
      video.load();
    }
    video.play()?.catch(() => {
      /* playback refused (e.g. battery saver): the poster stays */
    });
  }
}

if (!customElements.get('hero-video')) customElements.define('hero-video', HeroVideo);
